import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, Skeleton } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { actions as routerActions } from "redux-router5";
import ConnectedAppLayout from "../AppLayout";
import type { RootState } from "../store";
import ModalFooter from "../../common/components/ModalFooter";
import { PageTitleContainer } from "../../common/components/PageTitle";
import { routeNames } from "../common/routes";
import { projectActions } from "../../features/project/projectSlice";
import type { Client, ProjectDetailed } from "../generated-axios-client";
import { ProjectFields } from "./ProjectFields";

type EditProjectPageProps = {
	dispatch: Dispatch;
	intl: IntlShape;
	clientItems: Client[];
	project: ProjectDetailed | null;
};

export const EditProjectPage = ({
	dispatch,
	intl,
	clientItems,
	project,
}: EditProjectPageProps) => {
	const [form] = Form.useForm();

	const initialValues = useMemo(() => {
		if (!project) return undefined;
		return {
			name: project.name,
			client: project.client?.id ?? null,
			project_status: project.project_status ?? null,
			start: project.start ? dayjs(project.start) : null,
			end: project.end ? dayjs(project.end) : null,
		};
	}, [project]);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	if (!project || !clientItems) {
		return <Skeleton active />;
	}

	const save = () => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					projectActions.addOrUpdateProject({
						...values,
						id: project.id,
						start: (values.start as Dayjs | null)?.format("YYYY-MM-DD") ?? null,
						end: (values.end as Dayjs | null)?.format("YYYY-MM-DD") ?? null,
					}),
				);
			})
			.catch(() => {});
	};

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "project_edit_title" })}
			>
				<Form form={form} layout="vertical" initialValues={initialValues}>
					<ProjectFields intl={intl} clientItems={clientItems} />
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(
								routerActions.navigateTo(routeNames.PROJECT_VIEW, {
									id: project.id,
								}),
							)
						}
					>
						{intl.formatMessage({ id: "ticket_button_title_cancel" })}
					</Button>
					<Button type="primary" onClick={save}>
						{intl.formatMessage({ id: "ticket_button_title_create" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

export const ConnectedEditProjectPage = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	clientItems: state.project.clients,
	project: state.project.project,
}))(EditProjectPage);

export default ConnectedEditProjectPage;
